import { useState } from 'react';
import { apiFetch } from '../../lib/api.js';
import { useToast } from '../Toast.js';

interface ClassEntry {
  name: string;
  days: string[];
  time: string;
  instructor?: string;
  level?: string;
}

interface Props {
  orgId: string;
  initial: { timezone?: string; classes?: ClassEntry[] } | null;
  onSaved: () => void;
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function ScheduleSection({ orgId, initial, onSaved }: Props) {
  const { toast } = useToast();
  const [classes, setClasses] = useState<ClassEntry[]>(initial?.classes ?? []);
  const [isSaving, setIsSaving] = useState(false);

  const save = async () => {
    setIsSaving(true);
    try {
      await apiFetch(`/orgs/${orgId}/site-content/schedule`, {
        method: 'PUT',
        body: JSON.stringify({ classes: classes.filter(c => c.name && c.time) }),
      });
      toast('success', 'Schedule saved — site rebuilding');
      onSaved();
    } catch { toast('error', 'Failed to save'); }
    finally { setIsSaving(false); }
  };

  const toggleDay = (idx: number, day: string) => {
    const updated = [...classes];
    const entry = updated[idx]!;
    if (entry.days.includes(day)) {
      entry.days = entry.days.filter(d => d !== day);
    } else {
      entry.days = [...entry.days, day];
    }
    setClasses(updated);
  };

  return (
    <div className="space-y-3">
      {classes.map((cls, i) => (
        <div key={i} className="p-3 bg-gray-50 rounded-lg space-y-2">
          <div className="flex gap-2">
            <input type="text" value={cls.name} placeholder="Class name"
              onChange={e => { const n = [...classes]; n[i] = { ...cls, name: e.target.value }; setClasses(n); }}
              className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium" />
            <input type="text" value={cls.time} placeholder="4:30 PM - 5:30 PM"
              onChange={e => { const n = [...classes]; n[i] = { ...cls, time: e.target.value }; setClasses(n); }}
              className="w-40 rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
            <button onClick={() => setClasses(classes.filter((_, j) => j !== i))} className="text-xs text-red-400 hover:text-red-600 px-2">×</button>
          </div>
          <div className="flex gap-1 flex-wrap">
            {DAYS.map(day => (
              <button key={day} type="button" onClick={() => toggleDay(i, day)}
                className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                  cls.days.includes(day) ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                }`}>
                {day}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input type="text" value={cls.instructor ?? ''} placeholder="Instructor (optional)"
              onChange={e => { const n = [...classes]; n[i] = { ...cls, instructor: e.target.value || undefined }; setClasses(n); }}
              className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs" />
            <input type="text" value={cls.level ?? ''} placeholder="Level (optional)"
              onChange={e => { const n = [...classes]; n[i] = { ...cls, level: e.target.value || undefined }; setClasses(n); }}
              className="w-32 rounded-lg border border-gray-200 px-3 py-1.5 text-xs" />
          </div>
        </div>
      ))}
      {classes.length < 30 && (
        <button onClick={() => setClasses([...classes, { name: '', days: [], time: '', instructor: '', level: '' }])}
          className="text-sm text-primary-600 hover:text-primary-800 font-medium">+ Add class</button>
      )}
      <div>
        <button onClick={save} disabled={isSaving}
          className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors">
          {isSaving ? 'Saving...' : 'Save Schedule'}
        </button>
      </div>
    </div>
  );
}
